import { createSuccessfulAttempt, createUnsuccessfulAttempt, Try } from '../../../types/Try';
import { AssertionException } from '../../../utils/log';
import { Guid } from '../../model/guid';
import { AppStore } from '../../models/Store';
import { ItemsReducerState } from './itemsReducer';
import {
  ensureIsMenuItem,
  ensureSectionOrSequencedFlowItem,
  filterOutNullsy,
  isHotspot,
  isMenuItem,
  isNonSequencedFlowItem,
  isQuiz,
  isResourcesFolder,
  isSection,
  isSequencedFlowItem,
  isSequencedFlowStep,
  Item,
  ItemHelper,
  MenuItem,
  SequencedFlowStep,
} from '@ael/core-api';
import { List, OrderedMap } from 'immutable';
import memoizee from 'memoizee';
import { createSelector } from 'reselect';

export const getItemsStore: (store: AppStore) => OrderedMap<string, Item> = (state: AppStore) => state.items;

const itemsToList = (orderedItems: OrderedMap<string, Item>): List<Item> => orderedItems.toList();
const memoizedItemsToList = memoizee(itemsToList, { max: 1 });
export const getItemsList = createSelector(getItemsStore, (items) => memoizedItemsToList(items));

export type GetItemById = (itemId: Guid) => Item;
export type GetItemByIdAllowUndefined = (itemId: Guid) => Item | undefined;

const getItemByIdAllowUndefined =
  (items: ItemsReducerState): GetItemByIdAllowUndefined =>
  (itemId: Guid): Item | undefined => {
    const item = items.get(itemId);

    return item;
  };

/**
 *   @deprecated Do not use this in new code, it can mask nasty bugs
 *   Use getItemByIdTyped instead ideally
 */
export const getItemByIdAllowUndefinedSelector = createSelector(getItemsStore, getItemByIdAllowUndefined);

const getItemById = (getItemByIdAllowUndefined: GetItemByIdAllowUndefined) => (itemId: Guid) => {
  const item = getItemByIdAllowUndefined(itemId);
  if (!item) {
    throw AssertionException(`Item id ${itemId} not found.`);
  }

  return item;
};

export const getItemByIdSelector = createSelector(getItemByIdAllowUndefinedSelector, getItemById);

export type GetItemByIdTyped = <T extends Item>(itemId: Guid, typeguard: (item: Item) => item is T) => T;

const getItemByIdTyped =
  (getItemById: (itemId: Guid) => Item): GetItemByIdTyped =>
  <T extends Item>(itemId: Guid, typeguard: (item: Item) => item is T): T => {
    const item = getItemById(itemId);
    if (!typeguard(item)) {
      throw AssertionException(`Unexpected type for itemId ${itemId}, type is ${item.type}`);
    }

    return item;
  };

export const getItemByIdTypedSelector = createSelector(getItemByIdSelector, getItemByIdTyped);

export const getParentSelector = createSelector(getItemByIdSelector, (getItemById) => (item: Item) => {
  if (!item.parentId.parentId) {
    return null;
  }

  return getItemById(item.parentId.parentId);
});

export const getContentItemParentSelector = createSelector(getParentSelector, (getParent) => (item: Item) => {
  let parent: Item | null = item;
  do {
    parent = getParent(parent);
    if (!parent) {
      // This is not necessarily true for resources, but I'm keeping it for now.
      throw AssertionException('Unexpected, content item should always have parent.');
    }
  } while (ItemHelper.isContentItem(parent));

  return parent;
});

export const getContainingMenuItemIdSelector = createSelector(getParentSelector, (getParent) => (item: Item) => {
  let parent = getParent(item);
  while (parent !== null && !isMenuItem(parent)) {
    parent = getParent(parent);
  }

  return parent?.id ?? null;
});

export const getParentSubmenuByChildIdSelector = createSelector(
  getItemsStore,
  (state: ItemsReducerState) =>
    (itemId: Guid): Try<MenuItem> => {
      const maybeItem = state.find((item: Item) => isMenuItem(item) && item.childItems.contains(itemId));
      if (!maybeItem) {
        return createUnsuccessfulAttempt<MenuItem>();
      }
      ensureIsMenuItem(maybeItem);
      return createSuccessfulAttempt(maybeItem);
    }
);

const getParentSubmenuItemsListByChild =
  (getParentSubmenuByChildId: (itemId: Guid) => Try<MenuItem>) =>
  (itemId: Guid): List<string> => {
    const item = getParentSubmenuByChildId(itemId);
    if (item.success) {
      const parentsOfParent = getParentSubmenuItemsListByChild(getParentSubmenuByChildId)(item.value.id);
      return List<string>(parentsOfParent).push(item.value.id);
    }
    return List<string>();
  };

export const getParentSubmenuItemsListByChildSelector = createSelector(
  getParentSubmenuByChildIdSelector,
  getParentSubmenuItemsListByChild
);

export type SearchResult = {
  items: FoundMenuItem[];
  contentItems: FoundContentItem[];
};

export type FoundItem = {
  match: Match;
  item: Item;
};

export type FoundContentItem = FoundItem & {
  parentMenuItemTitle: string | undefined;
};

export type FoundMenuItem = FoundItem & {
  childrenCount: number;
};

type Match = {
  titleSegments: MatchSegment[];
  referenceSegments: MatchSegment[] | null;
};

export type MatchSegment = {
  value: string;
  matches: boolean;
};

const appendOnlyNonEmptyString = (segment: MatchSegment, segments: MatchSegment[]): MatchSegment[] => {
  if (segment.value === '') {
    return segments;
  }

  return [...segments, segment];
};

const createMatchSegment = (value: string, matches: boolean): MatchSegment => ({
  value,
  matches,
});

const segmentMatch = (title: string, matchBeginningIndex: number, searchPhrase: string) => {
  const titleStart = title.slice(0, matchBeginningIndex);
  const foundPhraseEnd = matchBeginningIndex + searchPhrase.length;
  const matchingPhrase = title.slice(matchBeginningIndex, foundPhraseEnd);
  const titleEnd = title.slice(foundPhraseEnd);

  return appendOnlyNonEmptyString(createMatchSegment(titleEnd, false), [
    ...appendOnlyNonEmptyString(createMatchSegment(titleStart, false), []),
    { value: matchingPhrase, matches: true },
  ]);
};

const getPhrasePositionInText = (text: string, phrase: string): number | null => {
  const result = text.toLowerCase().indexOf(phrase.toLowerCase());
  if (result === -1) {
    return null;
  }

  return result;
};

const itemsTitleMatchesSearchPhrase = (
  title: string,
  reference: string | null,
  searchedText: string,
  projectReference: string
): Match | null => {
  const searchedTextPositionInTitle = getPhrasePositionInText(title, searchedText);
  if (searchedTextPositionInTitle !== null) {
    return {
      titleSegments: segmentMatch(title, searchedTextPositionInTitle, searchedText),
      referenceSegments: reference ? [createMatchSegment(reference, false)] : null,
    };
  }
  if (reference === null) {
    return null;
  }

  const searchedTextPositionInReference = getPhrasePositionInText(reference, searchedText);
  if (searchedTextPositionInReference !== null) {
    return {
      titleSegments: [createMatchSegment(title, false)],
      referenceSegments: segmentMatch(reference, searchedTextPositionInReference, searchedText),
    };
  }

  const projectReferenceWithHyphen = projectReference + '-';
  const trimmedSearchText = searchedText.slice(projectReferenceWithHyphen.length);
  if (trimmedSearchText.length === 0 || !searchedText.startsWith(projectReferenceWithHyphen)) {
    return null;
  }

  const positionWithTrimmedSearchText = getPhrasePositionInText(reference, trimmedSearchText);

  return positionWithTrimmedSearchText !== null
    ? {
        titleSegments: [createMatchSegment(title, false)],
        referenceSegments: segmentMatch(reference, positionWithTrimmedSearchText, trimmedSearchText),
      }
    : null;
};

const searchPhraseInItem = (item: Item, searchPhrase: string, projectReference: string) => {
  if (!item.title) {
    return null;
  }
  return itemsTitleMatchesSearchPhrase(
    item.title,
    ItemHelper.isContentItem(item) ? item.referenceIdentifier : null,
    searchPhrase,
    projectReference
  );
};

const isMenuItemOrSectionOrResourcesFolder = (item: Item) =>
  isMenuItem(item) || isSection(item) || isResourcesFolder(item);

function segregateItemsAndContentItems(foundItem: FoundItem, prev: SearchResult): SearchResult {
  if (ItemHelper.isContentItem(foundItem.item)) {
    return {
      items: prev.items,
      contentItems: [...prev.contentItems, foundItem as FoundContentItem],
    };
  }

  return {
    items: [...prev.items, foundItem as FoundMenuItem],
    contentItems: prev.contentItems,
  };
}

function addChildren(getItemById: (itemId: Guid) => Item) {
  return (itemIds: List<Guid>, itemId: Guid) => {
    const item = getItemById(itemId);
    if (!isSection(item)) {
      return itemIds.push(itemId);
    }
    const sectionChildren = getItemChildrenIds(getItemById, item).toArray();
    return itemIds.push(itemId, ...sectionChildren);
  };
}

const getItemChildrenIds = (getItemById: GetItemById, item: Item): List<Guid> => {
  const getSubitems = (itemIds: List<Guid>) => itemIds.reduce(addChildren(getItemById), List<Guid>());

  if (isSequencedFlowItem(item)) {
    const contentItems = item.contentItems;
    return getSubitems(contentItems);
  }
  if (isHotspot(item)) {
    return item.tabs;
  }
  if (isNonSequencedFlowItem(item)) {
    return item.hotspots;
  }
  if (isQuiz(item)) {
    return item.contentItems;
  }
  if (isMenuItem(item)) {
    return item.childItems;
  }
  if (isSection(item)) {
    return getSubitems(item.children);
  }
  if (isResourcesFolder(item)) {
    return item.resources;
  }

  throw AssertionException(`Unexpected item type, ${item.type}`);
};

const getChildrenCountSelector = createSelector(getItemByIdSelector, (getItemById: GetItemById) => (item: Item) => {
  return getItemChildrenIds(getItemById, item).size;
});

export const searchItems = (
  items: List<Item>,
  getParent: (item: Item) => Item,
  getChildrenCount: (item: Item) => number,
  searchPhrase: string,
  projectReference: string
): SearchResult => {
  const matchingItems = filterOutNullsy(
    items
      .filter((item: Item) => !isMenuItemOrSectionOrResourcesFolder(item))
      .map((item: Item) => {
        const match = searchPhraseInItem(item, searchPhrase, projectReference);
        return !match ? null : { item, match };
      })
      .toArray()
  ).reduce(
    (acc: SearchResult, foundItem) => {
      return segregateItemsAndContentItems(foundItem, acc);
    },
    {
      items: [],
      contentItems: [],
    }
  );

  const results = {
    contentItems: matchingItems.contentItems.map(
      (foundItem): FoundContentItem => ({
        item: foundItem.item,
        match: foundItem.match,
        parentMenuItemTitle: getParent(foundItem.item).title,
      })
    ),
    items: matchingItems.items.map(
      (foundItem): FoundMenuItem => ({
        item: foundItem.item,
        match: foundItem.match,
        childrenCount: getChildrenCount(foundItem.item),
      })
    ),
  };
  return results;
};
export const searchItemsSelector = createSelector(
  getItemsList,
  getContentItemParentSelector,
  getChildrenCountSelector,
  (items, getParent: (item: Item) => Item, getChildrenCount: (item: Item) => number) =>
    (searchPhrase: string, projectReference: string): SearchResult =>
      searchItems(items, getParent, getChildrenCount, searchPhrase, projectReference)
);

export const nextAndPreviousSequencedFlowStepSelector = createSelector(
  getParentSelector,
  getItemByIdSelector,
  (getParent, getItemById: GetItemById) => (sequencedFlowStep: SequencedFlowStep) => {
    const currentParent = getParent(sequencedFlowStep);
    if (!currentParent) {
      throw AssertionException('Current item ID is null.');
    }
    ensureSectionOrSequencedFlowItem(currentParent);
    const childrenItems = isSequencedFlowItem(currentParent) ? currentParent.contentItems : currentParent.children;
    const currentStepIndex = childrenItems.indexOf(sequencedFlowStep.id);

    let nextStep: SequencedFlowStep | undefined;

    const nextStepId = childrenItems.get(currentStepIndex + 1);
    if (nextStepId) {
      const sequencedFlowChildItem = getItemById(nextStepId);
      if (isSequencedFlowStep(sequencedFlowChildItem)) {
        nextStep = sequencedFlowChildItem;
      }
    }

    let previousStep: SequencedFlowStep | undefined;
    if (currentStepIndex > 0) {
      const previousStepId = childrenItems.get(currentStepIndex - 1);
      if (previousStepId) {
        const sequencedFlowChildItem = getItemById(previousStepId);
        if (isSequencedFlowStep(sequencedFlowChildItem)) {
          previousStep = sequencedFlowChildItem;
        }
      }
    }

    return { nextStep: nextStep, previousStep: previousStep };
  }
);
